import React from "react";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  BiFirstPage,
  BiChevronLeft,
  BiChevronRight,
  BiLastPage,
} from "react-icons/bi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";

type TablePaginationProps = {
  pageSize: number;
  totalCount: number;
  pageNumber: number;
  pageIndicators?: number;
  loading?: boolean;
  onPageChange?: (num: number) => void;
};

const getLastPageNumber = (totalCount: number, pageSize: number): number => {
  return Math.ceil(totalCount / pageSize);
};

const getPageNumbers = (
  pageNumber: number,
  pageSize: number,
  totalCount: number,
  pageIndicators: number = 3
): number[] => {
  const lastPage = getLastPageNumber(totalCount, pageSize);
  if (lastPage === 0) return [];

  const half = Math.floor(pageIndicators / 2);
  let startPageNumber = Math.max(1, pageNumber - half);
  let endPageNumber = startPageNumber + pageIndicators - 1;

  if (endPageNumber > lastPage) {
    endPageNumber = lastPage;
    startPageNumber = Math.max(1, endPageNumber - pageIndicators + 1);
  }

  return Array.from(
    { length: endPageNumber - startPageNumber + 1 },
    (_, index) => startPageNumber + index
  );
};
const handleEnterKey =
  (callback: () => void) => (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === "Enter") {
      callback();
    }
  };

const TablePagination: React.FC<TablePaginationProps> = ({
  pageNumber,
  totalCount,
  pageSize,
  pageIndicators,
  loading = false,
  onPageChange,
}) => {
  const lastPageNumber = getLastPageNumber(totalCount, pageSize);

  return (
    <Stack
      component="footer"
      direction={{
        xs: "column",
        md: "row",
      }}
      alignItems="center"
      spacing={2}
      py={2}
      px={2}
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Stack flex={1} direction="row" alignItems="center" spacing={2}>
        <Box>
          <IconButton
            aria-label="First Page"
            disabled={pageNumber === 1}
            onClick={() => onPageChange(1)}
          >
            <BiFirstPage />
          </IconButton>
          <IconButton
            aria-label="Previous Page"
            disabled={pageNumber === 1}
            onClick={() => onPageChange(pageNumber - 1)}
          >
            <BiChevronLeft />
          </IconButton>
          {getPageNumbers(pageNumber, pageSize, totalCount, pageIndicators).map(
            (number) => (
              <Box
                component="span"
                key={number}
                onClick={() => onPageChange(number)}
                onKeyUp={handleEnterKey(() => onPageChange(number))}
                tabIndex={0}
                sx={{
                  margin: "0 4px",
                  cursor: "pointer",
                  fontWeight: pageNumber === number ? "bold" : "normal",
                  color: pageNumber === number ? "#fff" : undefined,
                  backgroundColor:
                    pageNumber === number ? "#0376a8" : "transparent",
                  height: "25px",
                  width: "25px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                }}
              >
                {number}
              </Box>
            )
          )}
          <IconButton
            aria-label="Next Page"
            disabled={lastPageNumber === 0 || pageNumber === lastPageNumber}
            onClick={() => onPageChange(pageNumber + 1)}
          >
            <BiChevronRight />
          </IconButton>
          <IconButton
            aria-label="Last Page"
            disabled={lastPageNumber === 0 || pageNumber === lastPageNumber}
            onClick={() => onPageChange(lastPageNumber)}
          >
            <BiLastPage />
          </IconButton>
          {loading && (
            <Box component="span" ml={2}>
              <FontAwesomeIcon
                icon={faCircleNotch}
                aria-label="Currently Loading Content"
                size="2x"
                spin
              />
            </Box>
          )}
        </Box>
      </Stack>
      <Box>
        <Typography variant="body2" className="page-info">
          {totalCount === 0
            ? ""
            : lastPageNumber === 0
            ? 0
            : `${pageNumber} of ${lastPageNumber} pages (${totalCount} items)`}
        </Typography>
      </Box>
    </Stack>
  );
};

export default TablePagination;
